import { BudgetExpenseOrIncome } from 'business/constants';
import { camelize } from 'helpers/syntaxHelper';
import React from 'react';
import {
    BudgetProposalT,
    ResourceSoftwareIdField,
} from 'routes/BudgetModule/config/types';
import { HttpError } from 'store/APIClient';
import {
    EditableValuesByResource,
    useGetEditableValuesByBudgetResource,
} from 'routes/BudgetModule/helpers/hooks/writeScopes';
import { getProjectionDetailsWithProjectionFields } from '../helpers/dataHelpers';
import {
    ProjectionT,
    VentilationsLabelMapping,
    AbstractProjectionDetailsT,
    EmptyProjectionDetails,
    AtterrissageProjectionDetails,
    PluriannualProjectionDetails,
    UseGetProjectionDetailsParams,
    UseGetProjectionDetailsResponse,
} from '../helpers/types';
import {
    extractSoftwareId,
    ProjectionResourceSoftwareId,
    useGetProjectionsForBudgetProposal,
} from './projection';
import { canEditResource } from 'routes/BudgetModule/helpers/writeScopesHelpers';

type ProjectionsBySoftwareId = Record<string, ProjectionT> | undefined;

type FetchProjectionDetailsHookParams = {
    budgetProposal: BudgetProposalT;
    resourceSoftwareIdField: ResourceSoftwareIdField;
    resourceSoftwareId: string | undefined;
    expenseOrIncome: BudgetExpenseOrIncome;
    useGetProjectionDetailsHook: (
        params: UseGetProjectionDetailsParams,
    ) => UseGetProjectionDetailsResponse;
    isDaf: boolean;
};

type ProjectionDetailsHookResponse = {
    isLoading: boolean;
    error: HttpError | null;
    isError: boolean;
    data:
        | (
              | EmptyProjectionDetails.WithMetadata
              | AtterrissageProjectionDetails.WithMetadata
              | PluriannualProjectionDetails.WithMetadata
          )[]
        | undefined;
    ventilationsLabelMapping: VentilationsLabelMapping | undefined;
};

/**
 * Returns Ventilations Label Mapping with camelized values
 */
const camelizeVentilationsLabelMapping = (
    ventilationsLabelMapping: VentilationsLabelMapping | undefined,
) =>
    ventilationsLabelMapping
        ? Object.keys(ventilationsLabelMapping).reduce(
              (acc, key: keyof VentilationsLabelMapping) => ({
                  ...acc,
                  [key]: camelize(ventilationsLabelMapping[key]),
              }),
              {} as VentilationsLabelMapping,
          )
        : undefined;

/**
 * Maps projections on resource (gestionnaire or org_chart_finance) software ids.
 */
const getProjectionsBySoftwareId = (
    projections: ProjectionT[] | undefined,
    resourceProjectionField: ProjectionResourceSoftwareId,
): ProjectionsBySoftwareId =>
    projections?.reduce<Record<string, ProjectionT>>((acc, projection) => {
        const softwareId = projection[resourceProjectionField];
        if (softwareId) {
            acc[softwareId] = projection;
        }
        return acc;
    }, {});

/**
 * Determines if a ProjectionDetail is editable, according to the user write access.
 * Append the CanEdit flag to the ProjectionDetail then returns it.
 */
const appendCanEditFlag = (
    projectionDetail:
        | EmptyProjectionDetails.WithProjectionData
        | AtterrissageProjectionDetails.FromEndpoint
        | PluriannualProjectionDetails.FromEndpoint,
    editableValuesByResource: EditableValuesByResource,
    resourceSoftwareIdField: ResourceSoftwareIdField,
    budgetProposal: BudgetProposalT,
): AbstractProjectionDetailsT => ({
    canEdit: canEditResource(
        editableValuesByResource,
        projectionDetail[resourceSoftwareIdField] ?? '',
        budgetProposal,
    ),
    ...projectionDetail,
});

/**
 * Hook to fetch projectionDetails and make sure they have
 * data regarding the projection, either by getting it directly from
 * the backend (for Resource view) or by getting it
 * from the projections list (for DAF view).
 * Is used for atterrissage and pluriannual projections.
 * */
export const useGetProjectionDetailsWithExtraFields = ({
    budgetProposal,
    resourceSoftwareId,
    resourceSoftwareIdField,
    expenseOrIncome,
    isDaf,
    useGetProjectionDetailsHook,
}: FetchProjectionDetailsHookParams): ProjectionDetailsHookResponse => {
    const {
        data: projections,
        isLoading: isProjectionsLoading,
        error: errorProjections,
        isError: isErrorProjections,
    } = useGetProjectionsForBudgetProposal({
        budgetProposalId: budgetProposal.id,
        enabled: isDaf,
    });
    const { data: editableValuesByResource } =
        useGetEditableValuesByBudgetResource();

    const resourceProjectionField = extractSoftwareId(resourceSoftwareIdField);
    const projectionsByResourceId = React.useMemo(
        () => getProjectionsBySoftwareId(projections, resourceProjectionField),
        [projections],
    );
    // on DAF view, we wait for all projections to be fetched (they can be created during the fetch)
    const shouldFetchProjectionDetails =
        !isDaf || Boolean(projections && projections.length > 0);

    const {
        data: projectionDetailsResult,
        isLoading: isProjectionDetailsLoading,
        error: errorProjectionDetails,
        isError: isErrorProjectionDetails,
    } = useGetProjectionDetailsHook({
        budgetProposalId: budgetProposal.id,
        resourceSoftwareId,
        expenseOrIncome,
        enabled: shouldFetchProjectionDetails,
    });

    const projectionDetailsWithProjectionFields =
        getProjectionDetailsWithProjectionFields({
            projectionsByResourceId,
            projectionDetailsData: projectionDetailsResult?.data,
            resourceSoftwareIdField,
        });

    const projectionDetails = React.useMemo(
        () =>
            projectionDetailsWithProjectionFields?.map((pd) =>
                appendCanEditFlag(
                    pd,
                    editableValuesByResource,
                    resourceSoftwareIdField,
                    budgetProposal,
                ),
            ),
        [
            projectionDetailsWithProjectionFields,
            resourceSoftwareIdField,
            editableValuesByResource,
            budgetProposal,
        ],
    );
    const camelizedVentilationsLabelMapping = camelizeVentilationsLabelMapping(
        projectionDetailsResult?.columns,
    );

    return {
        isLoading: isProjectionsLoading || isProjectionDetailsLoading,
        error: errorProjections || errorProjectionDetails,
        isError: isErrorProjections || isErrorProjectionDetails,
        data: projectionDetails,
        ventilationsLabelMapping: camelizedVentilationsLabelMapping,
    };
};
